import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Calendar, MapPin, Trophy, Users, ArrowLeft, CheckCircle2, Radio, Eye, EyeOff, ListOrdered, BarChart3, Search, Medal } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const ROUND_LABELS = { group: "Group", r32: "R32", r16: "R16", qf: "QF", sf: "SF", "3rd_place": "3rd Place", final: "Final" };
const ROUND_SORT = { group: 0, r32: 1, r16: 2, qf: 3, sf: 4, "3rd_place": 5, final: 6 };

function ageLbl(a) { return a === "19+" ? "Open (19+)" : a; }
function scoreStr(m) {
  const gs = m.games || [];
  if (!gs.length) return null;
  return gs.map((g) => `${g[0]}-${g[1]}`).join("  ·  ");
}

function OverallStandingsPanel({ tid, stEvent, stAge }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    const params = {};
    if (stEvent && stEvent !== "all") params.event_category = stEvent;
    if (stAge && stAge !== "all") params.age_category = stAge;
    api.get(`/tournaments/${tid}/overall-standings`, { params }).then((r) => setData(r.data)).catch(() => setData({ categories: [] }));
  }, [tid, stEvent, stAge]);
  if (!data || !data.categories.length) return null;
  return (
    <div className="mt-10 space-y-6" data-testid="overall-standings-panel">
      <div className="border-t-2 border-jpa-primary/60 pt-6">
        <div className="font-display text-2xl tracking-wider text-jpa-primary">OVERALL STANDINGS · FINAL POSITIONS</div>
        <p className="text-xs text-jpa-muted mt-1">Pool + knockout matches combined (2 pts per win in both). Bracket finishers rank first, then pool finish order. This drives the ranking-point allotment.</p>
      </div>
      {data.categories.map((c) => (
        <div key={`${c.event_category}__${c.age_category}`} data-testid={`overall-${c.event_category}-${c.age_category}`} className="border border-jpa-border rounded-sm bg-white overflow-hidden">
          <div className="bg-jpa-ink text-white px-4 py-3">
            <div className="text-[10px] font-bold uppercase tracking-widest text-white/70">{c.age_label}</div>
            <div className="font-display text-xl tracking-wider">{c.event_label} · OVERALL LEADERBOARD</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-jpa-surface/60 text-[10px] font-bold uppercase tracking-widest text-jpa-muted">
                <tr>
                  <th className="text-left py-2 pl-3 pr-1">#</th>
                  <th className="text-left py-2 px-1">Player</th>
                  <th className="text-left py-2 px-1">Pool</th>
                  <th className="text-center py-2 px-1">P</th>
                  <th className="text-center py-2 px-1">W-L</th>
                  <th className="text-center py-2 px-1">Pts</th>
                  <th className="text-center py-2 pr-3 pl-1">+/-</th>
                </tr>
              </thead>
              <tbody>
                {c.rows.map((r) => (
                  <tr key={`${r.entry_id}-${r.overall_rank}`} className={`border-b border-jpa-border/40 ${r.overall_rank === 1 ? "bg-amber-50" : r.overall_rank === 2 ? "bg-slate-50" : r.overall_rank === 3 ? "bg-orange-50" : ""}`}>
                    <td className="py-2 pl-3 pr-1 font-display text-lg text-jpa-primary">{r.overall_rank}</td>
                    <td className="py-2 px-1 font-bold">{r.name}</td>
                    <td className="py-2 px-1 text-xs text-jpa-muted">{r.pool_label}</td>
                    <td className="py-2 px-1 text-center">{r.played}</td>
                    <td className="py-2 px-1 text-center">{r.won}-{r.lost}</td>
                    <td className="py-2 px-1 text-center font-bold text-jpa-primary">{r.points}</td>
                    <td className={`py-2 px-1 pr-3 text-center font-bold ${r.diff > 0 ? "text-emerald-700" : r.diff < 0 ? "text-red-600" : ""}`}>{r.diff > 0 ? `+${r.diff}` : r.diff}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}


export default function TournamentDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [t, setT] = useState(null);
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState({ total: 0, unique_players: 0, groups: [] });
  const [gateOpen, setGateOpen] = useState(false);

  // ---- Fixtures area (on demand) ----
  const [showFx, setShowFx] = useState(false);
  const [fxTab, setFxTab] = useState("mymatches");
  const [fixtures, setFixtures] = useState({ matches: [], filters: { event_categories: [], age_categories: [] } });
  const [fxLoaded, setFxLoaded] = useState(false);
  const [fxEvent, setFxEvent] = useState("");    // "" = no filter chosen yet
  const [fxAge, setFxAge] = useState("");

  // ---- Standings ----
  const [standings, setStandings] = useState(null);
  const [stEvent, setStEvent] = useState("");
  const [stAge, setStAge] = useState("");

  // ---- Medals ----
  const [medals, setMedals] = useState(null);
  const [mdEvent, setMdEvent] = useState("");
  const [mdAge, setMdAge] = useState("");

  // ---- My Matches ----
  const [myName, setMyName] = useState("");
  const [myResults, setMyResults] = useState(null);
  const [myLoading, setMyLoading] = useState(false);

  useEffect(() => {
    api.get(`/tournaments/${id}`).then((r) => setT(r.data)).catch(() => setT(false));
    api.get(`/tournaments/${id}/entries-summary`).then((r) => setSummary(r.data)).catch(() => {});
  }, [id]);

  useEffect(() => {
    if (user) api.get("/me/entries").then((r) => setEntries(r.data)).catch(() => {});
  }, [user]);

  const loadFixtures = async (ev, ag) => {
    try {
      const params = {};
      if (ev && ev !== "all") params.event_category = ev;
      if (ag && ag !== "all") params.age_category = ag;
      const r = await api.get(`/tournaments/${id}/fixtures`, { params });
      setFixtures(r.data);
      setFxLoaded(true);
    } catch { setFixtures({ matches: [], filters: { event_categories: [], age_categories: [] } }); setFxLoaded(true); }
  };

  // On first opening the area, pull filter options (empty params returns full option lists)
  const loadFilterOptionsOnce = async () => {
    if (fixtures.filters.event_categories.length || fixtures.filters.age_categories.length) return;
    try {
      const r = await api.get(`/tournaments/${id}/fixtures`);
      setFixtures((f) => ({ ...f, filters: r.data.filters }));
    } catch {}
  };

  const loadStandings = async (ev, ag) => {
    try {
      const params = {};
      if (ev && ev !== "all") params.event_category = ev;
      if (ag && ag !== "all") params.age_category = ag;
      const r = await api.get(`/tournaments/${id}/standings`, { params });
      setStandings(r.data);
    } catch { setStandings({ categories: [] }); }
  };

  // Load fixtures only when at least one filter is picked
  useEffect(() => {
    if (!showFx) return;
    loadFilterOptionsOnce();
    if (!fxEvent && !fxAge) { setFixtures((f) => ({ ...f, matches: [] })); setFxLoaded(false); return; }
    loadFixtures(fxEvent, fxAge);
    // eslint-disable-next-line
  }, [showFx, fxEvent, fxAge]);

  // Standings: only load when a filter is picked; auto-refresh every 30s while filter active
  useEffect(() => {
    if (!showFx || fxTab !== "standings") return;
    loadFilterOptionsOnce();
    if (!stEvent && !stAge) { setStandings(null); return; }
    loadStandings(stEvent, stAge);
    const t = setInterval(() => loadStandings(stEvent, stAge), 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [showFx, fxTab, stEvent, stAge, id]);

  const loadMedals = async (ev, ag) => {
    try {
      const params = {};
      if (ev && ev !== "all") params.event_category = ev;
      if (ag && ag !== "all") params.age_category = ag;
      const r = await api.get(`/tournaments/${id}/medals`, { params });
      setMedals(r.data);
    } catch { setMedals({ categories: [] }); }
  };

  // Medals: auto-refresh every 30s while tab is active
  useEffect(() => {
    if (!showFx || fxTab !== "medals") return;
    loadFilterOptionsOnce();
    loadMedals(mdEvent, mdAge);
    const t = setInterval(() => loadMedals(mdEvent, mdAge), 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [showFx, fxTab, mdEvent, mdAge, id]);

  const searchMyMatches = async () => {
    const q = myName.trim();
    if (q.length < 3) { setMyResults({ error: "Type at least 3 characters", matches: [] }); return; }
    setMyLoading(true);
    try { const r = await api.get(`/tournaments/${id}/my-matches`, { params: { name: q } }); setMyResults(r.data); }
    catch (e) { setMyResults({ error: e.response?.data?.detail || "Search failed", matches: [] }); }
    finally { setMyLoading(false); }
  };

  const alreadyRegistered = entries.some((e) => e.tournament_id === id);
  const isOrganizer = user && (user.role === "admin" || (user.role === "club" && t?.organizer_club_id === user.club_id));

  const goJoin = () => {
    if (t.restrict_to_jharkhand) { setGateOpen(true); return; }
    nav(t.quick_registration ? `/tournaments/${id}/quick-join` : `/tournaments/${id}/join`);
  };

  // Group fixtures by single category; within a category: group matches first
  // (in placed order), then KO rounds in bracket order.
  const fxByCategory = useMemo(() => {
    const groups = {};
    for (const m of fixtures.matches || []) {
      const key = `${m.event_category}__${m.age_category}`;
      if (!groups[key]) groups[key] = { key, event_category: m.event_category, age_category: m.age_category, event_label: m.event_label, matches: [] };
      groups[key].matches.push(m);
    }
    for (const g of Object.values(groups)) {
      g.matches.sort((a, b) => (ROUND_SORT[a.round] ?? 9) - (ROUND_SORT[b.round] ?? 9) || (a.order ?? 0) - (b.order ?? 0));
    }
    return Object.values(groups).sort((a, b) => (a.age_category || "").localeCompare(b.age_category) || (a.event_category || "").localeCompare(b.event_category));
  }, [fixtures.matches]);

  // My Matches grouped by category
  const myByCategory = useMemo(() => {
    if (!myResults?.matches) return [];
    const groups = {};
    for (const m of myResults.matches) {
      const key = `${m.event_category}__${m.age_category}`;
      if (!groups[key]) groups[key] = { key, age_category: m.age_category, event_label: m.event_label, matches: [] };
      groups[key].matches.push(m);
    }
    return Object.values(groups);
  }, [myResults]);

  if (t === null) return <div className="p-20 text-center">Loading…</div>;
  if (t === false) return <div className="p-20 text-center">Tournament not found. <Link to="/tournaments" className="text-jpa-primary underline">Back</Link></div>;

  const matchRow = (m, i, highlightName = null) => {
    const isLive = m.status === "in_progress";
    const isDone = m.status === "completed" || m.status === "walkover";
    const sc = scoreStr(m);
    const nameCls = (nm) => {
      let c = "font-bold text-sm truncate";
      if (isDone && m.winner_side) c += nm === (m.winner_side === "a" ? m.player_a_name : m.player_b_name) ? " text-emerald-700" : " text-jpa-muted";
      if (highlightName && nm && nm.toLowerCase().includes(highlightName.toLowerCase())) c += " bg-jpa-accent/40 px-1 rounded-sm";
      return c;
    };
    return (
      <div key={m.id} data-testid={`fx-match-${m.id}`} className="px-4 py-3 flex items-center gap-3 flex-wrap">
        <div className="text-[10px] font-mono font-bold text-jpa-primary w-16 shrink-0">
          M{(m.order ?? i) + 1} · {ROUND_LABELS[m.round] || (m.round || "").toUpperCase()}
          {m.group_label ? <div className="text-jpa-muted mt-0.5">{m.group_label}</div> : null}
        </div>
        <div className="flex-1 min-w-0">
          <div className={nameCls(m.player_a_name)}>{m.player_a_name || "TBD"}</div>
          <div className="text-[11px] text-jpa-muted my-0.5">vs</div>
          <div className={nameCls(m.player_b_name)}>{m.player_b_name || "TBD"}</div>
        </div>
        <div className="text-right text-xs text-jpa-muted min-w-[110px]">
          {sc && <div className="font-display text-base tracking-wider text-jpa-ink">{sc}</div>}
          {m.court ? <div>Court {m.court}</div> : null}
          {m.scheduled_time ? <div>{m.scheduled_time}</div> : null}
          {isLive && <span className="inline-flex items-center gap-1 text-jpa-live font-bold text-[10px] uppercase tracking-widest"><Radio className="w-3 h-3" /> LIVE</span>}
          {isDone && m.winner_side && <span className="inline-block text-[10px] font-bold uppercase tracking-widest text-emerald-700">{(m.winner_side === "a" ? m.player_a_name : m.player_b_name) || "Winner"} wins</span>}
          {m.status === "walkover" && <span className="block text-[9px] uppercase tracking-widest text-amber-700">Walkover</span>}
        </div>
      </div>
    );
  };

  return (
    <div data-testid={`tournament-detail-${id}`}>
      <section className="relative h-[52vh] bg-jpa-ink overflow-hidden">
        {t.image_url && <img src={t.image_url} alt="" className="absolute inset-0 w-full h-full object-cover opacity-70" />}
        <div className="absolute inset-0 bg-gradient-to-t from-jpa-ink via-jpa-ink/70 to-transparent" />
        <div className="relative max-w-[1400px] mx-auto px-4 md:px-8 h-full flex flex-col justify-end pb-10 text-white">
          <Link to="/tournaments" className="inline-flex items-center gap-2 text-sm uppercase tracking-widest text-white/70 hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4" /> All tournaments
          </Link>
          <div className="flex flex-wrap gap-2">
            <span className={`inline-block w-fit px-2 py-1 text-[10px] font-bold uppercase tracking-widest skew-badge ${t.status === "running" ? "bg-jpa-live text-white live-pulse" : t.status === "upcoming" ? "bg-jpa-accent text-jpa-ink" : "bg-white/20 text-white"}`}>
              {t.status}
            </span>
            {t.division && <span className="bg-white/90 text-jpa-ink px-2 py-1 text-[10px] font-bold uppercase tracking-widest skew-badge">{t.division}</span>}
          </div>
          <h1 className="font-display text-4xl md:text-6xl lg:text-8xl tracking-wider mt-3 text-shadow-hero">{t.name}</h1>
        </div>
      </section>
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-14 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <h2 className="font-display text-3xl tracking-wider text-jpa-primary">About this event</h2>
          <p className="mt-4 text-jpa-muted leading-relaxed text-lg">{t.description}</p>
          <div className="mt-10 border-t border-jpa-border pt-8">
            <h3 className="font-display text-2xl tracking-wider">Categories & format</h3>
            <ul className="mt-3 grid md:grid-cols-2 gap-3 text-sm text-jpa-muted">
              <li>• Category: {t.category}</li>
              <li>• Duration: {t.start_date} → {t.end_date}</li>
              <li>• Division: {t.division || "state"}</li>
              <li>• Points count toward JPA state rankings</li>
              <li>• AIPA-sanctioned event</li>
            </ul>
          </div>
        </div>
        <aside className="border border-jpa-border p-6 rounded-sm h-fit lg:sticky lg:top-24 bg-white">
          <h3 className="font-display text-2xl tracking-wider mb-4">Event info</h3>
          <ul className="space-y-3 text-sm">
            <li className="flex gap-3"><MapPin className="w-4 h-4 text-jpa-primary mt-0.5" /> <span><b>Venue:</b> {t.location}</span></li>
            <li className="flex gap-3"><Calendar className="w-4 h-4 text-jpa-primary mt-0.5" /> <span><b>Dates:</b> {t.start_date} – {t.end_date}</span></li>
            <li className="flex gap-3"><Trophy className="w-4 h-4 text-jpa-primary mt-0.5" /> <span><b>Prize:</b> {t.prize_pool || "TBA"}</span></li>
            <li className="flex gap-3"><Users className="w-4 h-4 text-jpa-primary mt-0.5" /> <span><b>Entries:</b> {summary.unique_players != null ? `${summary.unique_players} players · ${summary.total} event entries` : summary.total}</span></li>
          </ul>
          {alreadyRegistered ? (
            <div data-testid="tournament-registered" className="mt-6 border border-green-300 bg-green-50 text-green-900 p-3 rounded-sm flex items-center gap-2 text-sm">
              <CheckCircle2 className="w-4 h-4" /> You&apos;re registered. Track status in <Link to="/profile" className="underline font-bold ml-1">your profile</Link>.
            </div>
          ) : t.registration_open && t.event_categories?.length ? (
            t.external_booking_url ? (
              <a href={t.external_booking_url} target="_blank" rel="noopener noreferrer">
                <Button data-testid="tournament-external-btn" className="w-full mt-6 bg-jpa-primary hover:bg-jpa-primary-hover text-white font-bold uppercase tracking-widest rounded-sm h-12">
                  Book externally ↗
                </Button>
              </a>
            ) : (
              <Button data-testid="tournament-join-btn" onClick={goJoin} className="w-full mt-6 bg-jpa-primary hover:bg-jpa-primary-hover text-white font-bold uppercase tracking-widest rounded-sm h-12">
                JOIN NOW
              </Button>
            )
          ) : (
            <div className="mt-6 text-center text-jpa-muted text-sm uppercase tracking-widest">Registration closed</div>
          )}
          {isOrganizer && (
            <Link to={`/organizer/tournaments/${id}`}>
              <Button data-testid="open-organizer-hub" variant="outline" className="w-full mt-3 rounded-sm text-xs uppercase tracking-widest font-bold">
                Open Organizer Hub
              </Button>
            </Link>
          )}
        </aside>
      </div>

      {/* ============= FIXTURES / STANDINGS / MY MATCHES (on demand) ============= */}
      <div data-testid="tournament-fixtures-area" className="max-w-[1400px] mx-auto px-4 md:px-8 pb-16">
        <div className="border-t border-jpa-border pt-10">
          {!showFx ? (
            <div className="text-center py-8">
              <Button data-testid="show-fixtures-btn" onClick={() => setShowFx(true)} className="rounded-sm bg-jpa-primary hover:bg-jpa-primary-hover text-white font-bold uppercase tracking-widest h-12 px-8 gap-2">
                <Eye className="w-4 h-4" /> Show Fixtures
              </Button>
              <p className="text-xs text-jpa-muted mt-3 uppercase tracking-widest">Fixtures · Standings · Find your matches</p>
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
                <div className="flex gap-1 flex-wrap">
                  <button data-testid="fx-tab-mymatches" onClick={() => setFxTab("mymatches")}
                    className={`inline-flex items-center gap-1.5 px-4 h-10 text-xs font-bold uppercase tracking-widest rounded-sm transition-colors ${fxTab === "mymatches" ? "bg-jpa-primary text-white" : "bg-white border border-jpa-border text-jpa-muted hover:border-jpa-primary"}`}>
                    <Search className="w-3.5 h-3.5" /> My Matches
                  </button>
                  <button data-testid="fx-tab-fixtures" onClick={() => setFxTab("fixtures")}
                    className={`inline-flex items-center gap-1.5 px-4 h-10 text-xs font-bold uppercase tracking-widest rounded-sm transition-colors ${fxTab === "fixtures" ? "bg-jpa-primary text-white" : "bg-white border border-jpa-border text-jpa-muted hover:border-jpa-primary"}`}>
                    <ListOrdered className="w-3.5 h-3.5" /> Fixtures
                  </button>
                  <button data-testid="fx-tab-standings" onClick={() => setFxTab("standings")}
                    className={`inline-flex items-center gap-1.5 px-4 h-10 text-xs font-bold uppercase tracking-widest rounded-sm transition-colors ${fxTab === "standings" ? "bg-jpa-primary text-white" : "bg-white border border-jpa-border text-jpa-muted hover:border-jpa-primary"}`}>
                    <BarChart3 className="w-3.5 h-3.5" /> Pools & Standings
                  </button>
                  <button data-testid="fx-tab-medals" onClick={() => setFxTab("medals")}
                    className={`inline-flex items-center gap-1.5 px-4 h-10 text-xs font-bold uppercase tracking-widest rounded-sm transition-colors ${fxTab === "medals" ? "bg-jpa-primary text-white" : "bg-white border border-jpa-border text-jpa-muted hover:border-jpa-primary"}`}>
                    <Medal className="w-3.5 h-3.5" /> Medals
                  </button>
                </div>
                <Button data-testid="hide-fixtures-btn" onClick={() => setShowFx(false)} variant="outline" className="rounded-sm text-xs uppercase tracking-widest font-bold gap-1.5">
                  <EyeOff className="w-3.5 h-3.5" /> Hide
                </Button>
              </div>

              {/* -------- FIXTURES TAB -------- */}
              {fxTab === "fixtures" && (
                <div data-testid="fx-fixtures-panel">
                  <div className="grid sm:grid-cols-2 gap-3 mb-6 max-w-2xl">
                    <div>
                      <label className="text-[10px] font-bold uppercase tracking-widest text-jpa-muted">Age category</label>
                      <Select value={fxAge || undefined} onValueChange={setFxAge}>
                        <SelectTrigger data-testid="fx-age-filter" className="mt-1 rounded-sm h-11 bg-white"><SelectValue placeholder="Pick age…" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All ages</SelectItem>
                          {fixtures.filters.age_categories.map((o) => <SelectItem key={o.value} value={o.value}>{ageLbl(o.value)}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-[10px] font-bold uppercase tracking-widest text-jpa-muted">Event</label>
                      <Select value={fxEvent || undefined} onValueChange={setFxEvent}>
                        <SelectTrigger data-testid="fx-event-filter" className="mt-1 rounded-sm h-11 bg-white"><SelectValue placeholder="Pick event…" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All events</SelectItem>
                          {fixtures.filters.event_categories.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {(!fxEvent && !fxAge) ? (
                    <div data-testid="fx-empty-hint" className="border border-dashed border-jpa-border p-8 text-center text-sm text-jpa-muted rounded-sm">Pick an Age or Event above to view fixtures.</div>
                  ) : !fxLoaded ? (
                    <div className="border border-dashed border-jpa-border p-8 text-center text-sm text-jpa-muted rounded-sm">Loading fixtures…</div>
                  ) : fxByCategory.length === 0 ? (
                    <div className="border border-dashed border-jpa-border p-8 text-center text-sm text-jpa-muted rounded-sm">No fixtures published for this filter yet.</div>
                  ) : (
                    <div className="space-y-8">
                      {fxByCategory.map((g) => (
                        <div key={g.key} data-testid={`fx-cat-${g.key}`} className="border border-jpa-border rounded-sm bg-white overflow-hidden">
                          <div className="bg-jpa-primary text-white px-4 py-3 flex items-center justify-between flex-wrap gap-2">
                            <div>
                              <div className="text-[10px] font-bold uppercase tracking-widest text-white/70">{ageLbl(g.age_category)}</div>
                              <div className="font-display text-xl tracking-wider">{g.event_label}</div>
                            </div>
                            <div className="text-[10px] font-bold uppercase tracking-widest">{g.matches.length} match{g.matches.length === 1 ? "" : "es"}</div>
                          </div>
                          <div className="divide-y divide-jpa-border">
                            {g.matches.map((m, i) => matchRow(m, i))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* -------- STANDINGS TAB -------- */}
              {fxTab === "standings" && (
                <div data-testid="fx-standings-panel">
                  <div className="grid sm:grid-cols-2 gap-3 mb-4 max-w-2xl">
                    <div>
                      <label className="text-[10px] font-bold uppercase tracking-widest text-jpa-muted">Age category</label>
                      <Select value={stAge || undefined} onValueChange={setStAge}>
                        <SelectTrigger data-testid="st-age-filter" className="mt-1 rounded-sm h-11 bg-white"><SelectValue placeholder="Pick age…" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All ages</SelectItem>
                          {fixtures.filters.age_categories.map((o) => <SelectItem key={o.value} value={o.value}>{ageLbl(o.value)}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-[10px] font-bold uppercase tracking-widest text-jpa-muted">Event</label>
                      <Select value={stEvent || undefined} onValueChange={setStEvent}>
                        <SelectTrigger data-testid="st-event-filter" className="mt-1 rounded-sm h-11 bg-white"><SelectValue placeholder="Pick event…" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All events</SelectItem>
                          {fixtures.filters.event_categories.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mb-4 text-[10px] uppercase tracking-widest text-jpa-muted">
                    <Radio className="w-3 h-3 text-jpa-live" /> Auto-refreshes every 30s · Ranked by Pool Points (2 per win) → Head-to-Head → Point Difference
                  </div>
                  {(!stEvent && !stAge) ? (
                    <div data-testid="st-empty-hint" className="border border-dashed border-jpa-border p-8 text-center text-sm text-jpa-muted rounded-sm">Pick an Age or Event above to view standings.</div>
                  ) : !standings ? (
                    <div className="border border-dashed border-jpa-border p-8 text-center text-sm text-jpa-muted rounded-sm">Loading standings…</div>
                  ) : standings.categories.length === 0 ? (
                    <div className="border border-dashed border-jpa-border p-8 text-center text-sm text-jpa-muted rounded-sm">No pool matches yet — standings appear once group matches are generated.</div>
                  ) : (
                    <div className="space-y-8">
                      {standings.categories.map((c) => (
                        <div key={`${c.event_category}__${c.age_category}`} data-testid={`standings-cat-${c.event_category}-${c.age_category}`}>
                          <div className="mb-3">
                            <div className="text-[10px] font-bold uppercase tracking-widest text-jpa-primary">{ageLbl(c.age_category)}</div>
                            <div className="font-display text-2xl tracking-wider">{c.event_label}</div>
                          </div>
                          <div className="grid md:grid-cols-2 gap-4">
                            {c.pools.map((p) => (
                              <div key={p.label} data-testid={`standings-pool-${c.event_category}-${c.age_category}-${p.label.replace(/\s+/g, "")}`} className="border border-jpa-border rounded-sm bg-white overflow-hidden">
                                <div className="bg-jpa-ink text-white px-4 py-2 text-xs font-bold uppercase tracking-widest">{p.label}</div>
                                <table className="w-full text-xs">
                                  <thead>
                                    <tr className="text-[9px] uppercase tracking-widest font-bold text-jpa-muted border-b border-jpa-border">
                                      <th className="text-left py-2 pl-3 pr-1">#</th>
                                      <th className="text-left py-2 px-1">Player / Team</th>
                                      <th className="text-center py-2 px-1">P</th>
                                      <th className="text-center py-2 px-1">W</th>
                                      <th className="text-center py-2 px-1">L</th>
                                      <th className="text-center py-2 px-1">Pts</th>
                                      <th className="text-center py-2 px-1">PF</th>
                                      <th className="text-center py-2 px-1">PA</th>
                                      <th className="text-center py-2 pl-1 pr-3">+/-</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {p.rows.map((r) => (
                                      <tr key={r.name} data-testid={`standings-row-${r.rank}`} className={`border-b border-jpa-border/40 ${r.rank <= 2 ? "bg-emerald-50/60" : ""}`}>
                                        <td className="py-2 pl-3 pr-1 font-mono font-bold text-jpa-primary">{r.rank}</td>
                                        <td className="py-2 px-1 font-bold truncate max-w-[140px]">{r.name}{r.rank <= 2 && <span className="ml-1 text-[8px] font-bold uppercase tracking-widest text-emerald-700">Q</span>}</td>
                                        <td className="py-2 px-1 text-center">{r.played}</td>
                                        <td className="py-2 px-1 text-center font-bold">{r.won}</td>
                                        <td className="py-2 px-1 text-center">{r.lost}</td>
                                        <td className="py-2 px-1 text-center font-display text-jpa-primary font-bold">{r.points ?? (r.won * 2)}</td>
                                        <td className="py-2 px-1 text-center">{r.pf}</td>
                                        <td className="py-2 px-1 text-center">{r.pa}</td>
                                        <td className={`py-2 pl-1 pr-3 text-center font-bold ${r.diff > 0 ? "text-emerald-700" : r.diff < 0 ? "text-red-600" : ""}`}>{r.diff > 0 ? `+${r.diff}` : r.diff}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                      {/* --- Overall Standings (all pools combined) --- */}
                      <OverallStandingsPanel tid={id} stEvent={stEvent} stAge={stAge} />
                    </div>
                  )}
                </div>
              )}

              {/* -------- MEDALS TAB -------- */}
              {fxTab === "medals" && (
                <div data-testid="fx-medals-panel">
                  <div className="grid sm:grid-cols-2 gap-3 mb-4 max-w-2xl">
                    <div>
                      <label className="text-[10px] font-bold uppercase tracking-widest text-jpa-muted">Age category</label>
                      <Select value={mdAge || undefined} onValueChange={setMdAge}>
                        <SelectTrigger data-testid="md-age-filter" className="mt-1 rounded-sm h-11 bg-white"><SelectValue placeholder="All ages" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All ages</SelectItem>
                          {fixtures.filters.age_categories.map((o) => <SelectItem key={o.value} value={o.value}>{ageLbl(o.value)}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-[10px] font-bold uppercase tracking-widest text-jpa-muted">Event</label>
                      <Select value={mdEvent || undefined} onValueChange={setMdEvent}>
                        <SelectTrigger data-testid="md-event-filter" className="mt-1 rounded-sm h-11 bg-white"><SelectValue placeholder="All events" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All events</SelectItem>
                          {fixtures.filters.event_categories.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {!medals ? (
                    <div className="border border-dashed border-jpa-border p-8 text-center text-sm text-jpa-muted rounded-sm">Loading medals…</div>
                  ) : medals.categories.length === 0 ? (
                    <div className="border border-dashed border-jpa-border p-8 text-center text-sm text-jpa-muted rounded-sm">No medals declared yet — the podium appears once a category's Final is completed.</div>
                  ) : (
                    <div className="space-y-6">
                      {medals.categories.map((c) => {
                        const m = c.medals;
                        const podium = [
                          { key: "gold", label: "Gold", bg: "bg-amber-400", ring: "ring-amber-500", name: m.gold?.name },
                          { key: "silver", label: "Silver", bg: "bg-slate-300", ring: "ring-slate-400", name: m.silver?.name },
                        ];
                        (m.bronze || []).forEach((b, i) => podium.push({ key: `bronze-${i}`, label: "Bronze", bg: "bg-orange-400", ring: "ring-orange-500", name: b?.name }));
                        if (m.fourth?.name) podium.push({ key: "fourth", label: "4th", bg: "bg-jpa-border", ring: "ring-jpa-muted", name: m.fourth.name });
                        return (
                          <div key={`${c.event_category}__${c.age_category}`} data-testid={`medals-cat-${c.event_category}-${c.age_category}`} className="border border-jpa-border rounded-sm bg-white overflow-hidden">
                            <div className="bg-jpa-primary text-white px-4 py-3">
                              <div className="text-[10px] font-bold uppercase tracking-widest text-white/70">{c.age_label}</div>
                              <div className="font-display text-xl tracking-wider">{c.event_label}</div>
                            </div>
                            <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                              {podium.map((p) => (
                                <div key={p.key} data-testid={`medal-${p.key}`} className={`border border-jpa-border rounded-sm p-4 flex items-center gap-3 ring-1 ${p.ring}`}>
                                  <div className={`${p.bg} text-white w-12 h-12 rounded-full flex items-center justify-center shrink-0`}>
                                    <Medal className="w-6 h-6" />
                                  </div>
                                  <div className="min-w-0">
                                    <div className="text-[10px] font-bold uppercase tracking-widest text-jpa-muted">{p.label}</div>
                                    <div className="font-display text-base tracking-wider text-jpa-ink truncate">{p.name || "—"}</div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* -------- MY MATCHES TAB -------- */}
              {fxTab === "mymatches" && (
                <div data-testid="fx-mymatches-panel">
                  <div className="max-w-xl">
                    <label className="text-[10px] font-bold uppercase tracking-widest text-jpa-muted">Search your name in this tournament</label>
                    <div className="flex gap-2 mt-1">
                      <input
                        data-testid="mymatches-input"
                        value={myName}
                        onChange={(e) => setMyName(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && searchMyMatches()}
                        placeholder="Type your name (min 3 letters)…"
                        className="flex-1 border border-jpa-border rounded-sm h-11 px-3 text-sm bg-white focus:outline-none focus:border-jpa-primary"
                      />
                      <Button data-testid="mymatches-search-btn" onClick={searchMyMatches} disabled={myLoading} className="rounded-sm bg-jpa-primary hover:bg-jpa-primary-hover text-white text-xs uppercase tracking-widest font-bold h-11 px-5 gap-1.5">
                        <Search className="w-3.5 h-3.5" /> {myLoading ? "Searching…" : "Search"}
                      </Button>
                    </div>
                  </div>

                  {myResults && (
                    <div className="mt-6" data-testid="mymatches-results">
                      {myResults.error ? (
                        <div className="border border-amber-300 bg-amber-50 text-amber-800 text-sm rounded-sm px-4 py-3">{myResults.error}</div>
                      ) : myByCategory.length === 0 ? (
                        <div className="border border-dashed border-jpa-border p-8 text-center text-sm text-jpa-muted rounded-sm">No matches found for "{myResults.name}" in this tournament.</div>
                      ) : (
                        <div className="space-y-8">
                          <div className="text-xs text-jpa-muted uppercase tracking-widest">{myResults.matches.length} match{myResults.matches.length === 1 ? "" : "es"} found for "{myResults.name}"</div>
                          {myByCategory.map((g) => (
                            <div key={g.key} data-testid={`mymatches-cat-${g.key}`} className="border border-jpa-border rounded-sm bg-white overflow-hidden">
                              <div className="bg-jpa-primary text-white px-4 py-3 flex items-center justify-between flex-wrap gap-2">
                                <div>
                                  <div className="text-[10px] font-bold uppercase tracking-widest text-white/70">{ageLbl(g.age_category)}</div>
                                  <div className="font-display text-xl tracking-wider">{g.event_label}</div>
                                </div>
                                <div className="text-[10px] font-bold uppercase tracking-widest">{g.matches.length} match{g.matches.length === 1 ? "" : "es"}</div>
                              </div>
                              <div className="divide-y divide-jpa-border">
                                {g.matches.map((m, i) => matchRow(m, i, myResults.name))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {gateOpen && (
        <div data-testid="jharkhand-gate" className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-sm p-6 md:p-8 max-w-md w-full">
            <h2 className="font-display text-2xl md:text-3xl tracking-wider text-jpa-primary mb-3">Jharkhand players only</h2>
            <p className="text-jpa-muted text-sm leading-relaxed">
              This Tournament is Only For Players From Jharkhand. Please Do Not Register If you are not a Player from Jharkhand.
              <br /><br />
              <b>No Refund Of Registration fees will be issued on rejection of registration.</b>
            </p>
            <div className="mt-6 flex gap-2 justify-end">
              <Button data-testid="jharkhand-gate-cancel" variant="outline" className="rounded-sm uppercase tracking-widest text-xs font-bold" onClick={() => setGateOpen(false)}>Cancel</Button>
              <Button data-testid="jharkhand-gate-agree" className="bg-jpa-primary hover:bg-jpa-primary-hover text-white rounded-sm uppercase tracking-widest text-xs font-bold" onClick={() => { setGateOpen(false); nav(t.quick_registration ? `/tournaments/${id}/quick-join` : `/tournaments/${id}/join`); }}>I understand</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
